/**
 * `AggregationElectionFlow` — orchestrator for the aggregation
 * election flow state machine.
 *
 * Lifts subscriber + getState pattern from `auth/auth-flow.ts` +
 * `export/export-flow.ts` + `recovery/recovery-flow.ts`. State
 * transitions via discriminated-union reducer (`applyEvent` from
 * `./state.ts`); API calls via `IProjectionServiceClient`
 * aggregation-election endpoints per design memo PR #150 §3.
 *
 * **Phase A.** Wires UX-side state machine + API-shape surface.
 * Substantive integration with DEV2 federated query orchestrator
 * (per memo §3.2) + DEV6 cohort-flag service for substrate-event-
 * triggered cadence enablement (per memo §3.3) lands at Phase B
 * once those substrate services ship the corresponding endpoints.
 */
import { applyEvent, isTerminal, } from './state.js';
export class AggregationElectionFlow {
    options;
    state = { kind: 'intro' };
    subscribers = new Set();
    constructor(options) {
        this.options = options;
    }
    getState() {
        return this.state;
    }
    subscribe(callback) {
        this.subscribers.add(callback);
        callback(this.state);
        return () => this.subscribers.delete(callback);
    }
    dispatch(event) {
        try {
            this.state = applyEvent(this.state, event);
        }
        catch (e) {
            this.state = {
                kind: 'error',
                code: 'transition-error',
                message: e instanceof Error ? e.message : String(e),
            };
        }
        for (const sub of this.subscribers) {
            sub(this.state);
        }
    }
    /**
     * Loads holder inventory from the projection service. Idempotent on
     * re-fire: returns early if state is no longer `intro`. Guards
     * against the latent re-fire surface in
     * `<AggregationElectionView />`'s `useEffect` (deps `[props.flow]`
     * with eslint-disabled exhaustive-deps; comment "Only fire on
     * initial mount + on subsequent intro re-entries"); React StrictMode
     * double-invocation in dev surfaces this immediately, but any
     * production path where `props.flow` identity changes (parent
     * re-render that recreates flow; navigation away-and-back) would
     * re-fire identically. Issue #266 Finding 2 substrate fix per VP Eng
     * disposition; option (b) orchestrator idempotence.
     */
    async loadInventory() {
        if (this.state.kind !== 'intro') {
            return;
        }
        try {
            const inventory = await this.options.client.fetchHolderInventory({
                subjectPciHash: this.options.subjectPciHash,
                ...(this.options.tenantId !== undefined ? { tenantId: this.options.tenantId } : {}),
            });
            if (this.state.kind !== 'intro') {
                return;
            }
            this.dispatch({ kind: 'inventory-loaded', inventory });
        }
        catch (e) {
            if (this.state.kind !== 'intro') {
                return;
            }
            this.dispatch({
                kind: 'error',
                code: 'inventory-load-failed',
                message: e instanceof Error ? e.message : String(e),
            });
        }
    }
    selectHolders(holderIds) {
        this.dispatch({ kind: 'select-holders', holderIds });
    }
    beginPerHolderConfiguration() {
        this.dispatch({ kind: 'begin-per-holder-configuration' });
    }
    configureHolder(opts) {
        this.dispatch({
            kind: 'configure-holder',
            holderId: opts.holderId,
            dataCategories: opts.dataCategories,
            cadence: opts.cadence,
            retention: opts.retention,
        });
    }
    beginConfirmation() {
        this.dispatch({ kind: 'begin-confirmation' });
    }
    async confirmElection() {
        if (this.state.kind !== 'election-confirmation') {
            this.dispatch({
                kind: 'error',
                code: 'invalid-state',
                message: `confirmElection called in state ${this.state.kind}`,
            });
            return;
        }
        const configs = this.state.configs;
        this.dispatch({ kind: 'confirm-election' });
        try {
            const result = await this.options.client.scheduleAggregation({
                subjectPciHash: this.options.subjectPciHash,
                ...(this.options.tenantId !== undefined ? { tenantId: this.options.tenantId } : {}),
                elections: configs,
            });
            this.dispatch({
                kind: 'election-completed',
                initiatedFirstPullAt: result.initiatedFirstPullAt,
            });
        }
        catch (e) {
            this.dispatch({
                kind: 'error',
                code: 'schedule-aggregation-failed',
                message: e instanceof Error ? e.message : String(e),
            });
        }
    }
    cancel(reason) {
        if (isTerminal(this.state))
            return;
        this.dispatch({ kind: 'cancel', reason });
    }
}
//# sourceMappingURL=aggregation-flow.js.map